<template>
  <div class="question-add d-flex flex-column row-gap-6 layout-spacing">
    <h1 class="d-flex align-center">
      <AppTooltip>
        <template #activator="{ props }">
          <router-link
            :to="{ name: QUESTION_ANSWER_LIST_ROUTE_NAME }"
            v-bind="props"
            class="d-flex align-center mr-2"
            data-locator="question-answer-back-to-list"
          >
            <app-material-icon name="arrow-left" type="fa" size="sm" class="clickable ma-1" />
          </router-link>
        </template>
        <span>{{ $t('project.views.QuestionAnswerAdd.backToList') }}</span>
      </AppTooltip>
      <template v-if="isEditMode">
        {{ $t('project.views.QuestionAnswerEdit.title') }}
      </template>
      <template v-else>
        {{ $t('project.views.QuestionAnswerAdd.title') }}
      </template>
    </h1>

    <div v-if="isEditMode && questionPending" class="d-flex align-center justify-center h-100">
      <AppLoader />
    </div>
    <template v-else>
      <AppCard elevation="0" class="form-card pa-5">
        <!-- QUESTION -->
        <fieldset data-locator="question-answer-description-fieldset">
          <h3 class="step-label required-asterisk">
            {{ $t('project.views.QuestionAnswerAdd.steps.question') }}
          </h3>

          <div class="mb-3">
            <VisualTextEditor
              :value="question ? question.description : newQuestion.description"
              :placeholder="$t('project.views.QuestionAnswerAdd.description.placeholder')"
              :max-length="DESCRIPTION_MAX_LENGTH"
              clean-html-on-input
              qa-variant
              counter="footer"
              :qa-variant-error="v$.newQuestion.description.$invalid && v$.newQuestion.description.$dirty"
              @input="newQuestion.description = $event"
            />
          </div>
          <div v-if="descriptionErrors" class="mb-3">
            <div
              v-for="(errorMessage, index) in descriptionErrors"
              :key="index"
              class="text-error text-caption"
            >
              {{ errorMessage }}
            </div>
          </div>
        </fieldset>

        <!-- DOCUMENTS -->
        <fieldset
          data-locator="question-answer-documents-fieldset"
          class="mb-6"
        >
          <div class="qa-reference-documents">
            <app-button
              variant="text"
              color="primary"
              class="qa-reference-documents__trigger"
              data-locator="qa-question-open-documents-picker"
              @click="referenceDocumentDialogIsOpen = true"
            >
              <template #left-icon>
                <app-material-icon
                  name="plus"
                  type="fa"
                  size="xs"
                />
              </template>

              {{ $t('project.views.QuestionAnswerAdd.steps.documents') }}
            </app-button>

            <div
              v-if="selectedDocuments.length > 0"
              class="qa-reference-documents__list"
              data-locator="qa-question-selected-documents"
            >
              <DocumentsPickerSelectedItem
                v-for="doc in selectedDocuments"
                :key="`${doc.type}-${doc.id}`"
                :file="doc"
                layout="inline"
                locator-prefix="qa-question-selected-doc"
                @remove="removeSelectedDocument"
              />
            </div>
          </div>
        </fieldset>

        <!-- PRIORITY -->
        <fieldset data-locator="question-answer-priority-fieldset" class="mb-5">
          <h3 class="step-label required-asterisk">
            {{ $t('project.views.QuestionAnswerAdd.steps.priority') }}
          </h3>
          <QuestionAnswerPrioritySelect
            v-model="newQuestion.priority"
            :disabled="postQuestionPending || patchQuestionPending"
          />
        </fieldset>

        <!-- CATEGORY -->
        <fieldset data-locator="question-answer-category-fieldset">
          <h3 class="step-label required-asterisk">
            {{ $t('project.views.QuestionAnswerAdd.steps.category') }}
          </h3>
          <div>
            <QuestionAnswerCategoriesSelect
              :model-value="newQuestion.categories"
              @update:model-value="selectedCategoriesChanged"
            />
          </div>
        </fieldset>
      </AppCard>

      <!-- RIGHTS -->
      <AppCard elevation="0" class="form-card pa-5">
        <fieldset data-locator="question-answer-rights-fieldset">
          <h3 class="step-label">
            {{ $t('project.views.QuestionAnswerAdd.steps.rights') }}
          </h3>
          <div>
            <QuestionAnswerAddShare
              v-model:custom-message="newQuestion.customShareMessage"
              v-model:share-type="newQuestion.shareType"
              :custom-message-validation="v$.newQuestion.customShareMessage"
              isEditMode
            />
          </div>
        </fieldset>
      </AppCard>

      <!-- ACTIONS -->
      <div class="d-flex justify-end">
        <app-button
          type="outlined"
          variant="neutral"
          :to="{ name: QUESTION_ANSWER_LIST_ROUTE_NAME }"
          class="mr-4"
        >
          {{ $t('common.cancel') }}
        </app-button>
        <app-button
          data-locator="question-answer-publish-button"
          color="primary"
          :loading="isEditMode ? patchQuestionPending : postQuestionPending"
          @click="prepareSubmitQuestion"
        >
          <template v-if="isEditMode">
            {{ $t('project.views.QuestionAnswerEdit.saveModifications') }}
          </template>
          <template v-else>
            {{ $t('project.views.QuestionAnswerAdd.publish') }}
          </template>
        </app-button>
      </div>
    </template>

    <!-- DIALOGS -->
    <QuestionAnswerAddDocumentsDialog
      :is-open="referenceDocumentDialogIsOpen"
      :model-value="selectedDocuments"
      @update:isOpen="referenceDocumentDialogIsOpen = $event"
      @update:modelValue="selectedDocuments = $event"
      @confirm="onDocumentsPicked"
    />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import DocumentsPickerSelectedItem from '@/common/documents-picker/DocumentsPickerSelectedItem.vue'
import { track, USER_ROLE } from '@/common/pendo/agent'
import { formFieldErrors } from '@/common/utils/form'
import { getRoomRouteType } from '@/common/utils/rooms'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { toast } from '@/common/utils/toast'
import { maxEscapedLength } from '@/common/utils/validators'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { QAPriority } from '@/project/question-answer/constants/qaPriority'
import QuestionAnswerAddDocumentsDialog from '@/project/question-answer/dialogs/QuestionAnswerAddDocumentsDialog.vue'
import QuestionAnswerAddShare, { validations as addShareValidation } from '@/project/question-answer/QuestionAnswerAddShare.vue'
import QuestionAnswerCategoriesSelect from '@/project/question-answer/QuestionAnswerCategoriesSelect.vue'
import QuestionAnswerPrioritySelect from '@/project/question-answer/QuestionAnswerPrioritySelect.vue'
import { QUESTION_ANSWER_LIST_ROUTE_NAME, ROOM_MEMBERS_ROUTE_NAME, QUESTION_ANSWER_ITEM_ROUTE_NAME } from '@/router'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'
import { QuestionAnswerMutationTypes } from '@/store/modules/question-answer/mutation_types'

const DESCRIPTION_MAX_LENGTH = 32000

export default {
  name: 'QuestionAnswerForm',
  components: {
    AppLoader,
    AppCard,
    AppTooltip,
    DocumentsPickerSelectedItem,
    QuestionAnswerAddShare,
    QuestionAnswerCategoriesSelect,
    QuestionAnswerAddDocumentsDialog,
    VisualTextEditor,
    QuestionAnswerPrioritySelect,
  },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    newQuestion: {
      description: {
        required,
        maxLength: maxEscapedLength(DESCRIPTION_MAX_LENGTH),
      },
      ...addShareValidation,
    },
  },
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    mnemo: {
      type: String,
      required: false,
    },
    questionId: {
      type: [Number, String],
      required: false,
    },
  },
  data () {
    return {
      DESCRIPTION_MAX_LENGTH,
      QUESTION_ANSWER_LIST_ROUTE_NAME,
      newQuestion: {
        description: '',
        status: 'draft',
        priority: QAPriority.MEDIUM,
        categories: [],
        customShareMessage: '',
        shareType: SHARE_TYPE_OPTIONS.CUSTOM,
      },
      selectedDocuments: [],
      referenceDocumentDialogIsOpen: false,
      QAPriority,
    }
  },
  head () {
    return {
      title: this.isEditMode
        ? this.$t('project.views.QuestionAnswerEdit.metaTitle')
        : this.$t('project.views.QuestionAnswerAdd.metaTitle'),
    }
  },
  computed: {
    ...mapState('questionAnswer', [
      'question',
      'questionPending',
      'postQuestionPending',
      'patchQuestionPending',
    ]),
    ...mapState('documentSharePicker', ['selectedGroups', 'selectedMembers']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['roomMnemo', 'isCurrentUserPm']),
    questionForm () {
      return this.v$.newQuestion
    },
    descriptionErrors () {
      return formFieldErrors(
        this.questionForm.description,
        [
          [
            'required',
            this.$t('project.views.QuestionAnswerAdd.validations.descriptionRequired'),
          ],
        ],
      )
    },
  },
  created () {
    if (!this.currentRoom.isDataroom) {
      this.$router.replace({
        name: ROOM_MEMBERS_ROUTE_NAME,
        params: { roomType: getRoomRouteType(this.currentRoom) },
      })
    } else if (this.isEditMode) {
      this.prepareGetQuestion()
    }
  },
  beforeUnmount () {
    if (this.isEditMode) {
      this.SET_QUESTION(null)
      this.RESET_SELECTED_GROUPS_AND_MEMBERS()
    }
  },
  methods: {
    ...mapActions('questionAnswer', [
      QuestionAnswerActionTypes.GET_QUESTION,
      QuestionAnswerActionTypes.POST_QUESTION,
      QuestionAnswerActionTypes.PATCH_QUESTION,
    ]),
    ...mapMutations('questionAnswer', [QuestionAnswerMutationTypes.SET_QUESTION]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.RESET_SELECTED_GROUPS_AND_MEMBERS,
    ]),
    onDocumentsPicked (documents) {
      this.selectedDocuments = Array.isArray(documents) ? documents : []
    },
    removeSelectedDocument (doc) {
      this.selectedDocuments = this.selectedDocuments.filter(
        item => !(item?.id === doc?.id && item?.type === doc?.type),
      )
    },
    normalizeQuestionDocument (doc, type) {
      if (!doc) return null
      return {
        ...doc,
        type,
        name: doc.name ?? doc.basename,
        basename: doc.basename ?? doc.name,
      }
    },
    async prepareGetQuestion () {
      try {
        await this.GET_QUESTION({
          mnemo: this.mnemo,
          questionId: this.questionId,
        })

        const files = (this.question?.files ?? [])
          .filter(Boolean)
          .map(file => this.normalizeQuestionDocument(file, 'file'))

        const folders = (this.question?.directories ?? [])
          .filter(Boolean)
          .map(folder => this.normalizeQuestionDocument(folder, 'folder'))

        this.selectedDocuments = [...files, ...folders]

        this.newQuestion = {
          ...this.newQuestion,
          ...this.question,
          priority: this.question?.priority || QAPriority.MEDIUM,
          shareType: this.question.rights.isPublic
            ? SHARE_TYPE_OPTIONS.PUBLIC
            : SHARE_TYPE_OPTIONS.CUSTOM,
        }

        this.INIT_SELECTED_GROUPS(this.question.rights.groups)
        this.INIT_SELECTED_MEMBERS(this.question.rights.users)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.views.QuestionAnswerItem.getQuestionError'))
        this.$router.push({ name: QUESTION_ANSWER_LIST_ROUTE_NAME })
      }
    },
    async prepareSubmitQuestion () {
      try {
        this.v$.$touch()
        if (this.v$.$invalid) return

        const { customShareMessage, ...questionRest } = this.newQuestion

        const fileIds = this.selectedDocuments
          .filter(doc => doc.type === 'file')
          .map(doc => doc.id)

        const folderIds = this.selectedDocuments
          .filter(doc => doc.type === 'folder')
          .map(doc => doc.id)

        const questionObject = {
          ...questionRest,
          customMessage: customShareMessage,
          withGroups: this.selectedGroups.map(g => g.id),
          withUsers: this.selectedMembers.map(u => u.id),
          fileIds,
          folderIds,
        }

        if (this.isEditMode) {
          await this.PATCH_QUESTION({
            mnemo: this.roomMnemo,
            questionId: this.questionId,
            data: questionObject,
          })
          toast.success(this.$t('project.views.QuestionAnswerEdit.editQuestionSuccess'))
          this.$router.push({
            name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
            params: {
              questionId: this.questionId,
            },
          })
        } else {
          const newQuestionData = await this.POST_QUESTION({
            mnemo: this.roomMnemo,
            data: questionObject,
          })
          toast.success(this.$t('project.views.QuestionAnswerAdd.postQuestionSuccess'))
          track('question_created', {
            project_mnemo: this.roomMnemo,
            user_role: this.isCurrentUserPm ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
            question_id: newQuestionData.data.question.id,
          })
          this.$router.push({ name: QUESTION_ANSWER_LIST_ROUTE_NAME })
        }
      } catch (error) {
        console.error(error)
        if (this.isEditMode) {
          toast.error(this.$t('project.views.QuestionAnswerEdit.editQuestionError'))
        } else {
          toast.error(this.$t('project.views.QuestionAnswerAdd.postQuestionError'))
        }
      }
    },
    selectedCategoriesChanged (categories) {
      this.newQuestion.categories = categories
    },
  },
}
</script>

<style scoped lang="scss">
.form-card {
  border: 1px solid rgb(var(--v-theme-grey-100));
  border-radius: 16px;
}

fieldset {
  border: none;
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgb(var(--v-theme-grey-900));
}

.text-error {
  font-size: 14px !important;
}

.qa-reference-documents {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.qa-reference-documents__trigger {
  min-height: 28px !important;
  padding: 0 8px !important;
}

.qa-reference-documents__trigger:hover {
  background: #0B0C0D0D !important;
}

.qa-reference-documents__list {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 16px 12px;
}

:deep(.qa-reference-documents__list .dp-selected-item) {
  width: 280px;
  height: 44px;
}
</style>
